
import { Vue, Component } from 'vue-property-decorator';
import eventBus from '@/features/common/eventBus';
import eventNames from '@/features/common/constants/events';
import ChangesSaved from '@/features/settings/components/ChangesSaved.vue';
import { copyTextToClipboard, showSuccessAlertDialog } from '@/features/common/helpers';
import { generateNewAPIKey, getApiKeys, updateAPIScopes } from '@/features/api-keys/services/apiKeys.services';
import { ApiKeyScopeEnum } from '@/features/api-keys/types';

@Component({
  components: {
    ChangesSaved,
  },
})
export default class ApiKeys extends Vue {
  private isApiKeyCopied = false;

  private isGenerating = false;

  private formData = {
    canAccessRates: false,
    canCreateShipment: false,
    canAccessUsers: false,
    apiKey: '',
  }

  private showAlert = {
    success: false,
    show: false,
  };

  async created(): Promise<void> {
    await this.fetchApiKeys();
  }

  private async fetchApiKeys() {
    const apiKeysResponse = await getApiKeys();

    if (apiKeysResponse.successful && apiKeysResponse.data) {
      const { apiKey, scopes } = apiKeysResponse.data;
      this.setAPIKeysData(apiKey, scopes);
    }
  }

  private async updateApiKeyScopes() {
    const scopes = this.prepareScopes();

    const response = await updateAPIScopes(scopes);

    this.showAlert = {
      show: true,
      success: response.successful,
    };
  }

  private submit() {
    eventBus.$once(eventNames.CONFIRMATION_DIALOG, this.generateAPIKey);

    this.$store.commit('toggleConfirmDialog', {
      show: true,
      content: this.$t('apiKeys.confirmationDialog.content'),
      btnText: this.$t('apiKeys.confirmationDialog.okButtonText'),
      title: this.$t('apiKeys.confirmationDialog.title'),
    });
  }

  private async generateAPIKey() {
    this.isGenerating = true;
    const response = await generateNewAPIKey();
    this.isGenerating = false;

    if (response.successful && response.data) {
      const { apiKey, scopes } = response.data;
      this.setAPIKeysData(apiKey, scopes);

      showSuccessAlertDialog(
        this.$t('apiKeys.alerts.apiKeyGeneratedSuccessfully') as string,
      );
    }
  }

  private setAPIKeysData(apiKey: string, scopes: ApiKeyScopeEnum[]) {
    this.formData.apiKey = apiKey;
    this.formData.canAccessRates = scopes.includes(ApiKeyScopeEnum.Rates);
    this.formData.canCreateShipment = scopes.includes(ApiKeyScopeEnum.CreateShipment);
    this.formData.canAccessUsers = scopes.includes(ApiKeyScopeEnum.Users);
  }

  private prepareScopes(): ApiKeyScopeEnum[] {
    const scopes: ApiKeyScopeEnum[] = [];

    if (this.formData.canAccessRates) {
      scopes.push(ApiKeyScopeEnum.Rates);
    }
    if (this.formData.canCreateShipment) {
      scopes.push(ApiKeyScopeEnum.CreateShipment);
    }
    if (this.formData.canAccessUsers) {
      scopes.push(ApiKeyScopeEnum.Users);
    }

    return scopes;
  }

  private getLabel(isAccess: boolean): string {
    const label = isAccess ? this.$t('labels.on') : this.$t('labels.off');
    return label as string;
  }

  private copyApiKeyToClipboard() {
    copyTextToClipboard(this.formData.apiKey);

    this.isApiKeyCopied = true;
    setTimeout(() => {
      this.isApiKeyCopied = false;
    }, 3000);
  }
}
